// Shade matching pages (start, results and shade pages): the shade picker,
// the visitor's saved shades with Undo, results filters and "Show more".
// The server renders every page; this script swaps in fresh HTML after each
// change instead of re-implementing the results in the browser.

const MAX_ENTRIES = 10
const TOAST_MS = 8000
const BRAND_RESULTS = 40

const ICON = {
  check: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"></path></svg>',
  chevron: '<svg class="mml-row__chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6"></path></svg>',
  search: '<svg class="mml-search__icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7"></circle><path d="M20 20l-3.5-3.5"></path></svg>',
  close: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12"></path><path d="M18 6L6 18"></path></svg>',
  plus: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14"></path><path d="M5 12h14"></path></svg>',
  arrow: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14"></path><path d="M13 6l6 6-6 6"></path></svg>'
}

const STEP_LABELS = { brand: 'Brand', product: 'Product', shade: 'Shade' }
const QUERY_KEYS = { brand: 'qBrand', product: 'qProduct', shade: 'qShade' }

// ---------------------------------------------------------------- helpers

function esc(value) {
  return String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))
}

function number(n) {
  return Number(n || 0).toLocaleString('en-US')
}

function plural(n, one, many) {
  return number(n) + ' ' + (n === 1 ? one : many)
}

// Lowercase, no accents or punctuation, so "L'Oréal" matches "loreal"
function norm(value) {
  return String(value || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
}

function hit(name, query) {
  if (!query) return true
  if (query.split(' ').every((term) => name.indexOf(term) !== -1)) return true
  return name.replace(/ /g, '').indexOf(query.replace(/ /g, '')) !== -1
}

function validHex(hex) {
  return typeof hex === 'string' && /^#[0-9a-f]{3,8}$/i.test(hex) ? hex : null
}

function swatch(hex, extraClass) {
  const color = validHex(hex)
  const classes = 'mml-swatch' + (color ? '' : ' mml-swatch--none') + (extraClass ? ' ' + extraClass : '')
  return '<span class="' + classes + '"' + (color ? ' style="background-color: ' + color + '"' : '') + ' aria-hidden="true"></span>'
}

function all(selector, root) {
  return Array.prototype.slice.call((root || document).querySelectorAll(selector))
}

function touchDevice() {
  return window.matchMedia && window.matchMedia('(pointer: coarse)').matches
}

function readJSON(element, name, fallback) {
  try {
    return JSON.parse(element.getAttribute(name)) || fallback
  } catch (e) {
    return fallback
  }
}

function parseHTML(html) {
  return new DOMParser().parseFromString(html, 'text/html')
}

// ---------------------------------------------------------------- network

function csrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta ? meta.getAttribute('content') : ''
}

function request(url, options) {
  return fetch(url, Object.assign({ credentials: 'same-origin' }, options)).then((response) => {
    if (!response.ok) throw new Error('HTTP ' + response.status)
    return response
  })
}

function getJSON(url) {
  return request(url, { headers: { Accept: 'application/json' } }).then((response) => response.json())
}

function sendJSON(method, url, body) {
  return request(url, {
    method: method,
    headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
    body: body ? JSON.stringify(body) : undefined
  }).then((response) => response.json())
}

function getHTML(url) {
  return request(url, { headers: { Accept: 'text/html' } }).then((response) => response.text())
}

// Changes run one at a time, each on the latest saved list
let queue = Promise.resolve()

function serial(task) {
  const run = queue.then(task, task)
  queue = run.catch(() => {})
  return run
}

// ---------------------------------------------------------------- saved shades

const Mine = {
  shades: [],
  skintones: [],

  set(state) {
    this.shades = (state && state.shades) || []
    this.skintones = (state && state.skintones) || []
  },

  count() {
    return this.shades.length + this.skintones.length
  },

  list(kind) {
    return kind === 'skintone' ? this.skintones : this.shades
  },

  has(kind, key) {
    return this.list(kind).some((entry) => entry.key === key)
  },

  find(kind, key) {
    return this.list(kind).filter((entry) => entry.key === key)[0]
  },

  snapshot() {
    return { shades: this.shades.slice(), skintones: this.skintones.slice() }
  },

  save(next) {
    return sendJSON('PUT', '/my_shades', {
      shades: next.shades.map((entry) => entry.key),
      skintones: next.skintones.map((entry) => entry.key)
    }).then((state) => {
      this.set(state)
      return state
    })
  },

  clear() {
    return sendJSON('DELETE', '/my_shades').then((state) => {
      this.set(state)
      return state
    })
  }
}

// Saves the list with one entry added or removed. Resolves with the list as
// it was before, for Undo.
function changeEntries(change) {
  return serial(() => {
    const previous = Mine.snapshot()
    const next = Mine.snapshot()
    change(next)
    return Mine.save(next).then(() => previous)
  })
}

function addEntry(entry) {
  return changeEntries((next) => {
    const list = entry.kind === 'skintone' ? next.skintones : next.shades
    if (!list.some((item) => item.key === entry.key)) list.push(entry)
  })
}

function removeEntry(kind, key) {
  return changeEntries((next) => {
    if (kind === 'skintone') next.skintones = next.skintones.filter((item) => item.key !== key)
    else next.shades = next.shades.filter((item) => item.key !== key)
  })
}

function restoreEntries(previous) {
  return serial(() => Mine.save(previous))
}

function tooManyEntries() {
  if (Mine.count() < MAX_ENTRIES) return false
  Toast.show('You can save up to ' + MAX_ENTRIES + ' shades. Remove one to add another.')
  return true
}

// ---------------------------------------------------------------- picker data

const Catalog = {
  brandIndex: null,
  brandsRequest: null,
  products: {},
  shades: {},

  loadBrands() {
    if (this.brandIndex) return Promise.resolve(this.brandIndex)
    if (!this.brandsRequest) {
      this.brandsRequest = getJSON('/api/v1/picker/brands').then((data) => {
        this.brandIndex = data.brands.map((brand) => ({ slug: brand[0], name: brand[1], count: brand[2], n: norm(brand[1]) }))
        return this.brandIndex
      })
      this.brandsRequest.catch(() => { this.brandsRequest = null })
    }
    return this.brandsRequest
  },

  loadProducts(slug) {
    if (this.products[slug]) return Promise.resolve(this.products[slug])
    return getJSON('/api/v1/picker/brands/' + encodeURIComponent(slug) + '/products').then((data) => {
      this.products[slug] = data.products
      return data.products
    })
  },

  loadShades(slug) {
    if (this.shades[slug]) return Promise.resolve(this.shades[slug])
    return getJSON('/api/v1/picker/products/' + encodeURIComponent(slug) + '/shades').then((data) => {
      this.shades[slug] = data.shades
      return data.shades
    })
  }
}

let pickerConfig = { featured: [], brandCount: '' }

function prefetchBrands() {
  const load = () => Catalog.loadBrands().catch(() => {})
  if (window.requestIdleCallback) window.requestIdleCallback(load, { timeout: 3000 })
  else setTimeout(load, 1500)
}

// ---------------------------------------------------------------- picker

// Brand, then product, then shade, plus the PANTONE SkinTone code form.
// options: { pantoneOpen, onPick(entry), onPantone(code) -> Promise<boolean> }
class Picker {
  constructor(root, options) {
    this.root = root
    this.options = options
    this.state = { brand: null, product: null, qBrand: '', qProduct: '', qShade: '', pantoneOpen: !!options.pantoneOpen, pantoneValue: '', pantoneError: '', busy: false }
    this.brandsError = false
    this.productList = null
    this.productError = false
    this.shadeList = null
    this.shadeError = false
    this.requests = 0

    root.addEventListener('click', (event) => this.onClick(event))
    root.addEventListener('input', (event) => this.onInput(event))
    root.addEventListener('keydown', (event) => this.onKeydown(event))

    this.render()
    prefetchBrands()
  }

  // ------------------------------------------------------------ rendering

  render() {
    const s = this.state
    const productState = !s.brand ? 'waiting' : (s.product ? 'done' : 'active')
    this.root.innerHTML =
      '<div class="mml-picker__steps">' +
        this.stepHTML('brand', 1, s.brand ? 'done' : 'active') +
        this.stepHTML('product', 2, productState) +
        this.stepHTML('shade', 3, s.product ? 'active' : 'waiting') +
      '</div>' +
      this.pantoneHTML()
  }

  stepHTML(step, position, state) {
    const s = this.state
    const labelId = 'mml-step-' + step
    const inputId = 'mml-q-' + step
    let html = '<section class="mml-step mml-step--' + step + ' is-' + state + '" aria-labelledby="' + labelId + '" data-step="' + step + '">'

    html += '<div class="mml-step__head">'
    html += '<span class="mml-step__dot" aria-hidden="true">' + (state === 'done' ? ICON.check : position) + '</span>'
    if (state === 'active') {
      html += '<label class="mml-step__label" id="' + labelId + '" for="' + inputId + '">' + STEP_LABELS[step] + '</label>'
    } else {
      html += '<span class="mml-step__label" id="' + labelId + '">' + STEP_LABELS[step] + '</span>'
    }
    if (state === 'waiting') html += '<span class="mml-step__hint">' + (step === 'product' ? 'Choose a brand first' : 'Then tap your shade') + '</span>'
    html += '</div>'

    if (state === 'done') {
      const value = step === 'brand' ? s.brand.name : s.product.name
      html += '<div class="mml-step__chosen"><span class="mml-step__value">' + esc(value) + '</span>' +
        '<button type="button" class="mml-link" data-mml-change="' + step + '" aria-label="Change ' + step + '">Change</button></div>'
    } else if (state === 'active') {
      html += '<div class="mml-search">' + ICON.search +
        '<input id="' + inputId + '" class="mml-input" type="search" autocomplete="off" autocapitalize="off" spellcheck="false" enterkeyhint="search"' +
        ' placeholder="' + esc(this.placeholder(step)) + '" value="' + esc(s[QUERY_KEYS[step]]) + '" data-mml-query="' + step + '"></div>'
      html += '<div class="mml-step__list" data-mml-list="' + step + '">' + this.listHTML(step) + '</div>'
    } else {
      html += '<p class="mml-step__wait">' + (step === 'product' ? 'Choose a brand first.' : 'Choose a product first.') + '</p>'
      html += step === 'product'
        ? '<div class="mml-skel mml-skel--rows" aria-hidden="true"><i></i><i></i><i></i></div>'
        : '<div class="mml-skel mml-skel--chips" aria-hidden="true"><i></i><i></i><i></i><i></i></div>'
    }

    return html + '</section>'
  }

  placeholder(step) {
    if (step === 'brand') return 'Search all ' + pickerConfig.brandCount + ' brands'
    if (step === 'product') return 'Search ' + this.state.brand.name + ' products'
    const list = this.shadeList || []
    const example = list.length ? list[Math.floor(list.length / 2)][1] : ''
    return example && example.length <= 14 ? 'Search shades, e.g. ' + example : 'Search shades'
  }

  listHTML(step) {
    if (step === 'brand') return this.brandListHTML()
    if (step === 'product') return this.productListHTML()
    return this.shadeListHTML()
  }

  rowHTML(kind, slug, name, meta, chevron) {
    return '<li><button type="button" class="mml-row" data-mml-pick="' + kind + '" data-slug="' + esc(slug) + '" data-name="' + esc(name) + '">' +
      '<span class="mml-row__name">' + esc(name) + '</span><span class="mml-row__meta">' + esc(meta) + '</span>' + (chevron ? ICON.chevron : '') + '</button></li>'
  }

  brandListHTML() {
    const s = this.state
    const query = norm(s.qBrand)
    const productCount = (n) => plural(n, 'product', 'products')

    if (!query) {
      return '<span class="mml-caption mml-caption--upper">Popular brands</span>' +
        '<ul class="mml-rows">' + pickerConfig.featured.map((brand) => this.rowHTML('brand', brand[0], brand[1], productCount(brand[2]), true)).join('') + '</ul>'
    }
    if (this.brandsError) return '<p class="mml-error">Couldn’t load the brand list. Reload the page to try again.</p>'
    if (!Catalog.brandIndex) return '<p class="mml-muted">Loading brands…</p>'

    const rank = (brand) => (brand.n === query ? 0 : (brand.n.indexOf(query) === 0 ? 1 : 2))
    const hits = Catalog.brandIndex.filter((brand) => hit(brand.n, query))
      .map((brand, index) => ({ brand: brand, index: index }))
      .sort((a, b) => (rank(a.brand) - rank(b.brand)) || (a.index - b.index))
      .map((item) => item.brand)

    if (!hits.length) return '<p class="mml-muted">No brand matches “' + esc(s.qBrand.trim()) + '”. Check the spelling or try fewer letters.</p>'

    const caption = hits.length > BRAND_RESULTS ? 'Top ' + BRAND_RESULTS + ' of ' + number(hits.length) + ', keep typing' : plural(hits.length, 'brand', 'brands')
    return '<span class="mml-caption">' + caption + '</span>' +
      '<ul class="mml-rows mml-scroll">' + hits.slice(0, BRAND_RESULTS).map((brand) => this.rowHTML('brand', brand.slug, brand.name, productCount(brand.count), true)).join('') + '</ul>'
  }

  productListHTML() {
    const s = this.state
    if (this.productError) return '<p class="mml-error">Couldn’t load the products. <button type="button" class="mml-link" data-mml-retry="product">Try again</button></p>'
    if (!this.productList) return '<p class="mml-muted">Loading products…</p>'
    if (!this.productList.length) return '<p class="mml-muted">No products with shades for ' + esc(s.brand.name) + ' yet.</p>'

    const row = (product) => this.rowHTML('product', product[0], product[1], plural(product[2], 'shade', 'shades') + (product[4] ? ' · Discontinued' : ''), false)
    const query = norm(s.qProduct)

    if (!query) {
      const popular = this.productList.filter((product) => product[3])
      const other = this.productList.filter((product) => !product[3])
      let html = ''
      if (popular.length) html += '<span class="mml-caption mml-caption--upper">Popular</span><ul class="mml-rows">' + popular.map(row).join('') + '</ul>'
      if (other.length) html += '<span class="mml-caption mml-caption--upper">' + (popular.length ? 'Other products' : 'All products') + ' · ' + number(other.length) + '</span><ul class="mml-rows">' + other.map(row).join('') + '</ul>'
      return '<div class="mml-scroll mml-scroll--products">' + html + '</div>'
    }

    const hits = this.productList.filter((product) => hit(norm(product[1]), query))
    if (!hits.length) return '<p class="mml-muted">No product matches “' + esc(s.qProduct.trim()) + '”.</p>'
    return '<div class="mml-scroll mml-scroll--products"><span class="mml-caption">' + plural(hits.length, 'product', 'products') + '</span><ul class="mml-rows">' + hits.map(row).join('') + '</ul></div>'
  }

  shadeListHTML() {
    const s = this.state
    if (this.shadeError) return '<p class="mml-error">Couldn’t load the shades. <button type="button" class="mml-link" data-mml-retry="shade">Try again</button></p>'
    if (!this.shadeList) return '<p class="mml-muted">Loading shades…</p>'

    const list = this.shadeList
    const query = norm(s.qShade)
    const hits = query ? list.filter((shade) => hit(norm(shade[1]), query)) : list
    const caption = query ? number(hits.length) + ' of ' + plural(list.length, 'shade', 'shades') : plural(list.length, 'shade', 'shades')
    // Swatches only help when some shades have a colour on file
    const colours = list.some((shade) => validHex(shade[2]))
    const legend = colours && list.some((shade) => !validHex(shade[2])) ? '<span class="mml-legend">' + swatch(null, 'mml-legend__swatch') + 'No colour on file</span>' : ''

    let html = '<div class="mml-captionrow"><span class="mml-caption">' + caption + '</span>' + legend + '</div>'
    if (!hits.length) return html + '<p class="mml-muted">No shade matches “' + esc(s.qShade.trim()) + '”.</p>'

    html += '<div class="mml-chips mml-scroll' + (colours ? ' mml-chips--swatches' : '') + '">' + hits.map((shade) => {
      const added = Mine.has('shade', shade[0])
      // Long names are cut to three lines; the tooltip has the rest
      return '<button type="button" class="mml-chip' + (added ? ' is-added' : '') + '"' + (added ? ' disabled' : '') + (shade[1].length > 30 ? ' title="' + esc(shade[1]) + '"' : '') +
        ' data-mml-pick="shade" data-slug="' + esc(shade[0]) + '" data-name="' + esc(shade[1]) + '" data-hex="' + esc(shade[2] || '') + '"' +
        ' aria-label="' + esc(added ? shade[1] + ', already in your shades' : 'Add ' + shade[1]) + '">' +
        (colours ? swatch(shade[2], 'mml-chip__swatch') : '') +
        '<span class="mml-chip__text"><span class="mml-chip__name">' + esc(shade[1]) + '</span>' + (added ? '<span class="mml-chip__added">Added</span>' : '') + '</span></button>'
    }).join('') + '</div>'
    return html
  }

  pantoneHTML() {
    const s = this.state
    if (!s.pantoneOpen) {
      return '<div class="mml-pantone"><button type="button" class="mml-link" data-mml-pantone="open">Know your PANTONE SkinTone code? Use it instead</button></div>'
    }
    const error = s.pantoneError
    return '<div class="mml-pantone is-open">' +
      '<label class="mml-pantone__label" for="mml-pantone-code">PANTONE SkinTone code</label>' +
      '<div class="mml-pantone__row">' +
        '<input id="mml-pantone-code" class="mml-input mml-pantone__input' + (error ? ' is-invalid' : '') + '" type="text" autocomplete="off" autocapitalize="characters" spellcheck="false" maxlength="8" placeholder="e.g. 1Y08"' +
        ' aria-describedby="mml-pantone-help"' + (error ? ' aria-invalid="true"' : '') + ' value="' + esc(s.pantoneValue) + '" data-mml-pantone-input>' +
        '<button type="button" class="mml-primary mml-primary--sm" data-mml-pantone="add"' + (s.busy ? ' disabled aria-busy="true"' : '') + '>' + (s.busy ? 'Adding…' : 'Add code') + '</button>' +
        '<button type="button" class="mml-link" data-mml-pantone="cancel">Cancel</button>' +
      '</div>' +
      '<p id="mml-pantone-help" class="mml-pantone__help' + (error ? ' is-error' : '') + '">' + esc(error || 'A number, Y or R, then two digits, as printed on your PANTONE SkinTone card.') + '</p>' +
    '</div>'
  }

  renderList(step) {
    const list = this.root.querySelector('[data-mml-list="' + step + '"]')
    if (list) list.innerHTML = this.listHTML(step)
  }

  renderPantone(focus) {
    const block = this.root.querySelector('.mml-pantone')
    if (!block) return
    block.outerHTML = this.pantoneHTML()
    if (focus) this.focus(focus)
  }

  focus(selector) {
    const element = this.root.querySelector(selector)
    if (element) element.focus()
  }

  // After a pick, go to the next step: focus its search box, except on touch
  // screens where that would cover the list with the keyboard
  focusStep(step) {
    const section = this.root.querySelector('[data-step="' + step + '"]')
    if (!section) return
    if (touchDevice()) {
      const top = section.getBoundingClientRect().top
      if (top < 0 || top > window.innerHeight - 120) section.scrollIntoView({ block: 'start', behavior: 'smooth' })
    } else {
      const input = section.querySelector('input')
      if (input) input.focus()
    }
  }

  // ------------------------------------------------------------ steps

  pickBrand(slug, name) {
    const s = this.state
    s.brand = { slug: slug, name: name }
    s.product = null
    s.qProduct = ''
    s.qShade = ''
    this.productList = Catalog.products[slug] || null
    this.productError = false
    this.render()
    this.focusStep('product')
    if (!this.productList) this.loadProducts()
  }

  loadProducts() {
    const s = this.state
    const request = ++this.requests
    this.productError = false
    Catalog.loadProducts(s.brand.slug).then((list) => {
      if (request !== this.requests) return
      this.productList = list
      this.renderList('product')
    }, () => {
      if (request !== this.requests) return
      this.productError = true
      this.renderList('product')
    })
  }

  pickProduct(slug, name) {
    const s = this.state
    s.product = { slug: slug, name: name }
    s.qShade = ''
    this.shadeList = Catalog.shades[slug] || null
    this.shadeError = false
    this.render()
    this.focusStep('shade')
    if (!this.shadeList) this.loadShades()
  }

  loadShades() {
    const s = this.state
    const request = ++this.requests
    this.shadeError = false
    Catalog.loadShades(s.product.slug).then((list) => {
      if (request !== this.requests) return
      this.shadeList = list
      this.renderList('shade')
      const input = this.root.querySelector('[data-mml-query="shade"]')
      if (input) input.setAttribute('placeholder', this.placeholder('shade'))
    }, () => {
      if (request !== this.requests) return
      this.shadeError = true
      this.renderList('shade')
    })
  }

  change(step) {
    const s = this.state
    this.requests++
    if (step === 'brand') {
      s.brand = null
      s.product = null
      s.qProduct = ''
      s.qShade = ''
    } else {
      s.product = null
      s.qShade = ''
    }
    this.render()
    this.focus('[data-mml-query="' + step + '"]')
  }

  pickShade(slug, name, hex) {
    const s = this.state
    if (!s.brand || !s.product || Mine.has('shade', slug)) return
    this.options.onPick({ kind: 'shade', key: slug, name: name, product: s.product.name, brand: s.brand.name, hex: validHex(hex) })
  }

  // ------------------------------------------------------------ PANTONE

  addPantone() {
    const s = this.state
    if (s.busy) return
    const code = s.pantoneValue.toUpperCase().replace(/\s+/g, '')
    if (!/^[1-9][YR][0-9]{2}$/.test(code)) return this.pantoneFail('That does not look like a PANTONE SkinTone code. Try one like 1Y08.')
    if (Mine.has('skintone', code)) return this.pantoneFail('That code is already in your shades.')

    s.busy = true
    this.renderPantone()
    Promise.resolve(this.options.onPantone(code)).then((saved) => {
      s.busy = false
      if (!this.root.isConnected) return
      if (saved === false) this.pantoneFail('We couldn’t find PANTONE SkinTone ' + code + '. Check the code on your card and try again.')
      else this.renderPantone()
    }, () => {
      s.busy = false
      if (this.root.isConnected) this.pantoneFail('Couldn’t save the code. Check your connection and try again.')
    })
  }

  pantoneFail(message) {
    this.state.pantoneError = message
    this.renderPantone('#mml-pantone-code')
  }

  // ------------------------------------------------------------ events

  onClick(event) {
    const target = event.target

    const pick = target.closest('[data-mml-pick]')
    if (pick) {
      if (pick.disabled) return
      const kind = pick.getAttribute('data-mml-pick')
      const slug = pick.getAttribute('data-slug')
      const name = pick.getAttribute('data-name')
      if (kind === 'brand') this.pickBrand(slug, name)
      else if (kind === 'product') this.pickProduct(slug, name)
      else this.pickShade(slug, name, pick.getAttribute('data-hex'))
      return
    }

    const change = target.closest('[data-mml-change]')
    if (change) return this.change(change.getAttribute('data-mml-change'))

    const retry = target.closest('[data-mml-retry]')
    if (retry) {
      if (retry.getAttribute('data-mml-retry') === 'product') {
        this.productError = false
        this.renderList('product')
        this.loadProducts()
      } else {
        this.shadeError = false
        this.renderList('shade')
        this.loadShades()
      }
      return
    }

    const pantone = target.closest('[data-mml-pantone]')
    if (pantone) {
      const action = pantone.getAttribute('data-mml-pantone')
      const s = this.state
      if (action === 'open') {
        s.pantoneOpen = true
        this.renderPantone('#mml-pantone-code')
      } else if (action === 'cancel') {
        s.pantoneOpen = false
        s.pantoneValue = ''
        s.pantoneError = ''
        this.renderPantone('[data-mml-pantone="open"]')
      } else {
        this.addPantone()
      }
    }
  }

  onInput(event) {
    const input = event.target
    const step = input.getAttribute('data-mml-query')
    if (step) {
      this.state[QUERY_KEYS[step]] = input.value
      if (step === 'brand' && input.value.trim() && !Catalog.brandIndex) this.loadBrands()
      this.renderList(step)
      return
    }
    if (input.hasAttribute('data-mml-pantone-input')) {
      this.state.pantoneValue = input.value
      if (this.state.pantoneError) {
        this.state.pantoneError = ''
        input.classList.remove('is-invalid')
        input.removeAttribute('aria-invalid')
        const help = this.root.querySelector('#mml-pantone-help')
        if (help) {
          help.classList.remove('is-error')
          help.textContent = 'A number, Y or R, then two digits, as printed on your PANTONE SkinTone card.'
        }
      }
    }
  }

  loadBrands() {
    this.brandsError = false
    Catalog.loadBrands().then(() => this.renderList('brand'), () => {
      this.brandsError = true
      this.renderList('brand')
    })
  }

  // Enter picks the only result; arrow keys move between rows
  onKeydown(event) {
    const target = event.target

    if (event.key === 'Enter') {
      if (target.hasAttribute('data-mml-pantone-input')) {
        event.preventDefault()
        this.addPantone()
        return
      }
      const step = target.getAttribute('data-mml-query')
      if (step) {
        event.preventDefault()
        const options = all('[data-mml-pick]:not([disabled])', this.root.querySelector('[data-mml-list="' + step + '"]') || this.root)
        if (options.length === 1) options[0].click()
      }
      return
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
    const section = target.closest('[data-step]')
    if (!section || section.getAttribute('data-step') === 'shade') return
    const rows = all('.mml-row', section)
    const input = section.querySelector('[data-mml-query]')
    const index = rows.indexOf(target)
    let next = null
    if (target === input && event.key === 'ArrowDown') next = rows[0]
    else if (index !== -1) next = event.key === 'ArrowDown' ? rows[index + 1] : (rows[index - 1] || input)
    if (next) {
      event.preventDefault()
      next.focus()
    }
  }
}

function pickerOptions(extra) {
  return Object.assign({ pantoneOpen: false }, extra)
}

// ---------------------------------------------------------------- toast

const Toast = {
  region: null,
  timer: null,
  undo: null,
  onHide: [],

  ensure() {
    if (this.region && this.region.isConnected) return this.region
    const region = document.createElement('div')
    region.className = 'mml-toast-region'
    region.setAttribute('role', 'status')
    region.setAttribute('aria-live', 'polite')
    region.addEventListener('click', (event) => {
      if (event.target.closest('[data-mml-undo]')) {
        const undo = this.undo
        this.hide()
        if (undo) undo()
      } else if (event.target.closest('[data-mml-dismiss]')) {
        this.hide()
      }
    })
    // Give people time to reach Undo
    region.addEventListener('mouseenter', () => this.pause())
    region.addEventListener('focusin', () => this.pause())
    region.addEventListener('mouseleave', () => this.resume())
    region.addEventListener('focusout', () => this.resume())
    document.body.appendChild(region)
    this.region = region
    return region
  },

  show(text, undo, onHide) {
    const region = this.ensure()
    this.hide()
    this.undo = undo || null
    if (onHide) this.onHide.push(onHide)
    region.innerHTML = '<div class="mml-toast"><span class="mml-toast__text">' + esc(text) + '</span>' +
      (undo ? '<button type="button" class="mml-toast__undo" data-mml-undo>Undo</button>' : '') +
      '<button type="button" class="mml-toast__close" aria-label="Dismiss" data-mml-dismiss>' + ICON.close + '</button></div>'
    this.resume()
  },

  pause() {
    clearTimeout(this.timer)
  },

  resume() {
    clearTimeout(this.timer)
    if (this.region && this.region.firstChild) this.timer = setTimeout(() => this.hide(), TOAST_MS)
  },

  hide() {
    clearTimeout(this.timer)
    this.undo = null
    if (this.region) this.region.innerHTML = ''
    const callbacks = this.onHide
    this.onHide = []
    callbacks.forEach((callback) => callback())
  }
}

// Pink "Added" highlight on the newest entry until the toast goes away.
// Returns the function that removes it.
function markNew(key) {
  const row = all('[data-entry-key]').filter((element) => element.getAttribute('data-entry-key') === key)[0]
  if (!row) return null
  row.classList.add('is-new')
  return () => row.classList.remove('is-new')
}

// ---------------------------------------------------------------- page swaps

function currentPage() {
  return document.getElementById('mml-page')
}

function pageType() {
  const page = currentPage()
  return page ? page.getAttribute('data-mml-page') : null
}

// Re-renders the page from the server. On the results page `view=results`
// keeps the results layout even when the last shade was removed.
function refresh(options) {
  const settings = Object.assign({ url: location.href, keepResults: pageType() === 'results' }, options)
  const url = new URL(settings.url, location.origin)
  url.searchParams.delete('page')
  if (settings.keepResults) url.searchParams.set('view', 'results')
  const page = currentPage()
  if (page) page.setAttribute('aria-busy', 'true')
  return getHTML(url.toString()).then(swap, (error) => {
    if (page) page.removeAttribute('aria-busy')
    throw error
  })
}

function swap(html) {
  const doc = parseHTML(html)
  const next = doc.getElementById('mml-page')
  const current = currentPage()
  if (!next || !current) {
    location.reload()
    return new Promise(() => {})
  }
  // DOMParser parses <noscript> content as markup
  all('noscript', next).forEach((element) => element.remove())
  current.replaceWith(next)
  if (doc.title) document.title = doc.title
  initPage(next)
  return next
}

let shownURL = location.pathname + location.search

// The filter control the visitor used, so focus can return to it after a swap
function focusedControl() {
  const active = document.activeElement
  const page = currentPage()
  if (!active || !page || !page.contains(active)) return null
  if (active.id) return { id: active.id }
  if (active.matches('a[href]')) return { href: active.getAttribute('href') }
  return {}
}

function refocus(control) {
  const page = currentPage()
  if (!control || !page) return
  let element = control.id ? document.getElementById(control.id) : null
  if (!element && control.href) element = all('a[href]', page).filter((link) => link.getAttribute('href') === control.href)[0]
  if (!element) {
    element = page.querySelector('.mml-matches')
    if (element) element.setAttribute('tabindex', '-1')
  }
  if (element) element.focus({ preventScroll: true })
}

function navigate(url) {
  const control = focusedControl()
  return refresh({ url: url }).then(() => {
    history.pushState({ mml: true }, '', url)
    shownURL = location.pathname + location.search
    refocus(control)
  }, () => {
    location.href = url
  })
}

function filtersURL(form) {
  const params = new URLSearchParams()
  all('input, select', form).forEach((field) => {
    if (field.name && field.value) params.append(field.name, field.value)
  })
  const query = params.toString()
  return form.getAttribute('action') + (query ? '?' + query : '')
}

function showMore(link) {
  if (link.getAttribute('aria-busy') === 'true') return
  const label = link.textContent
  link.setAttribute('aria-busy', 'true')
  link.textContent = 'Loading…'

  const url = new URL(link.href, location.origin)
  url.searchParams.set('view', 'results')
  getHTML(url.toString()).then((html) => {
    const doc = parseHTML(html)
    const grid = document.querySelector('[data-mml-cards]')
    const cards = all('[data-mml-cards] > *', doc)
    if (!grid) return
    cards.forEach((card) => grid.appendChild(card))

    const caption = document.querySelector('[data-mml-shown]')
    if (caption) {
      const total = Number(caption.getAttribute('data-total'))
      caption.textContent = 'Showing ' + number(grid.children.length) + ' of ' + plural(total, 'match', 'matches') + (caption.hasAttribute('data-filtered') ? ' with these filters' : '')
    }

    const nextLink = doc.querySelector('[data-mml-more]')
    if (nextLink) {
      link.setAttribute('href', nextLink.getAttribute('href'))
      link.textContent = label
      link.removeAttribute('aria-busy')
    } else {
      link.remove()
    }
    if (cards[0]) cards[0].focus({ preventScroll: true })
  }, () => {
    location.href = link.href
  })
}

// ---------------------------------------------------------------- results page

const Dialog = {
  element: null,
  opener: null,
  picker: null,

  build() {
    const element = document.createElement('div')
    element.className = 'mml-dialog'
    element.hidden = true
    element.innerHTML =
      '<div class="mml-dialog__backdrop" data-mml-dialog-close></div>' +
      '<div class="mml-dialog__panel" role="dialog" aria-modal="true" aria-labelledby="mml-dialog-title">' +
        '<span class="mml-dialog__handle" aria-hidden="true"></span>' +
        '<div class="mml-dialog__head">' +
          '<div class="mml-dialog__titles"><h2 class="mml-h2 mml-h2--lg" id="mml-dialog-title"></h2><p class="mml-muted" data-mml-dialog-sub></p></div>' +
          '<button type="button" class="mml-close" aria-label="Close" data-mml-dialog-close>' + ICON.close + '</button>' +
        '</div>' +
        '<div class="mml-picker" data-mml-dialog-picker></div>' +
      '</div>'
    element.addEventListener('click', (event) => {
      if (event.target.closest('[data-mml-dialog-close]')) this.close()
    })
    element.addEventListener('keydown', (event) => this.onKeydown(event))
    document.body.appendChild(element)
    this.element = element
  },

  open(opener, pantone) {
    if (!this.element || !this.element.isConnected) this.build()
    const element = this.element
    const hasEntries = Mine.count() > 0
    element.querySelector('#mml-dialog-title').textContent = hasEntries ? 'Add another shade you wear' : 'Add a shade you wear'
    element.querySelector('[data-mml-dialog-sub]').textContent = 'Brand, then product, then shade.' + (hasEntries ? ' Each shade you add improves your matches.' : '')

    this.opener = opener
    const host = document.createElement('div')
    host.className = 'mml-picker'
    host.setAttribute('data-mml-dialog-picker', '')
    element.querySelector('[data-mml-dialog-picker]').replaceWith(host)
    this.picker = new Picker(host, pickerOptions({
      pantoneOpen: pantone,
      onPick: (entry) => resultsAdd(entry),
      onPantone: (code) => resultsAddCode(code)
    }))

    element.hidden = false
    document.documentElement.classList.add('mml-locked')
    const page = currentPage()
    if (page) page.inert = true
    element.querySelector('.mml-dialog__panel').scrollTop = 0
    const first = element.querySelector(pantone ? '#mml-pantone-code' : '#mml-q-brand')
    if (first && !(touchDevice() && !pantone)) first.focus()
    else element.querySelector('.mml-close').focus()
  },

  isOpen() {
    return !!(this.element && !this.element.hidden)
  },

  close(restoreFocus) {
    if (!this.isOpen()) return
    this.element.hidden = true
    this.element.querySelector('[data-mml-dialog-picker]').innerHTML = ''
    this.picker = null
    document.documentElement.classList.remove('mml-locked')
    const page = currentPage()
    if (page) page.inert = false
    if (restoreFocus !== false && this.opener && this.opener.isConnected) this.opener.focus()
  },

  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault()
      this.close()
      return
    }
    if (event.key !== 'Tab') return
    const focusable = all('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])', this.element.querySelector('.mml-dialog__panel'))
      .filter((node) => node.offsetParent !== null)
    if (!focusable.length) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }
}

function focusAddButton() {
  const button = document.querySelector('#mml-page .mml-yours [data-mml-open-picker]')
  if (button) button.focus({ preventScroll: true })
}

// "Your shades" on results, the action panel on a shade page, the picker
// title on the start page
function focusMyShades() {
  const target = document.querySelector('#mml-page .mml-yours h2, #mml-page .mml-action, #mml-page #mml-picker-title')
  if (!target) return
  target.setAttribute('tabindex', '-1')
  target.focus({ preventScroll: true })
}

// Focus left with a control that a swap or the toast removed
function focusLost() {
  return !document.activeElement || document.activeElement === document.body
}

function undoTo(previous, options) {
  restoreEntries(previous)
    .then(() => refresh(options))
    .then(() => {
      if (focusLost()) focusMyShades()
    })
    .catch(() => Toast.show('Couldn’t undo. Check your connection and try again.'))
}

function resultsAdd(entry) {
  if (tooManyEntries()) return
  Dialog.close(false)
  addEntry(entry)
    .then((previous) => refresh().then(() => {
      focusAddButton()
      Toast.show('Added ' + entry.name, () => undoTo(previous), markNew(entry.key))
    }))
    .catch(() => Toast.show('Couldn’t add ' + entry.name + '. Check your connection and try again.'))
}

function resultsAddCode(code) {
  if (tooManyEntries()) return Promise.resolve(true)
  return addEntry({ kind: 'skintone', key: code, name: 'PANTONE ' + code }).then((previous) => {
    if (!Mine.has('skintone', code)) return false
    Dialog.close(false)
    return refresh().then(() => {
      focusAddButton()
      Toast.show('Added PANTONE ' + code, () => undoTo(previous), markNew(code))
      return true
    })
  })
}

function removeFromPage(kind, key) {
  const entry = Mine.find(kind, key)
  const name = entry ? entry.name : (kind === 'skintone' ? 'PANTONE ' + key : 'Shade')
  removeEntry(kind, key)
    .then((previous) => refresh().then(() => {
      focusMyShades()
      Toast.show(name + ' removed', () => undoTo(previous))
    }))
    .catch(() => Toast.show('Couldn’t remove ' + name + '. Check your connection and try again.'))
}

function startOver() {
  serial(() => {
    const previous = Mine.snapshot()
    return Mine.clear().then(() => previous)
  }).then((previous) => refresh({ url: '/', keepResults: false }).then(() => {
    history.replaceState(null, '', '/')
    shownURL = '/'
    window.scrollTo(0, 0)
    focusMyShades()
    if (previous.shades.length + previous.skintones.length) {
      Toast.show('Started over', () => undoTo(previous, { url: '/', keepResults: false }))
    }
  })).catch(() => Toast.show('Couldn’t start over. Check your connection and try again.'))
}

// ---------------------------------------------------------------- shade page

function addFromShadePage(form) {
  if (tooManyEntries()) return
  const data = form.dataset
  const entry = { kind: 'shade', key: data.mmlAddShade, name: data.name, product: data.product, brand: data.brand, hex: validHex(data.hex) }
  const button = form.querySelector('button')
  if (button) button.disabled = true
  addEntry(entry)
    .then((previous) => refresh().then(() => {
      focusMyShades()
      Toast.show('Added ' + entry.name, () => undoTo(previous))
    }))
    .catch(() => {
      if (button) button.disabled = false
      Toast.show('Couldn’t add ' + entry.name + '. Check your connection and try again.')
    })
}

function revealMore(button) {
  const list = document.getElementById(button.getAttribute('aria-controls'))
  if (!list) return
  const firstHidden = list.querySelector('.is-extra a, .is-extra')
  list.classList.add('is-expanded')
  button.parentNode.remove()
  if (firstHidden) {
    const link = firstHidden.matches('a') ? firstHidden : firstHidden.querySelector('a')
    if (link) link.focus()
  }
}

function openModal(id) {
  const modal = document.getElementById(id)
  if (!modal) return
  watchModal(modal)
  document.documentElement.classList.add('is-clipped')
  modal.classList.add('is-active')
  const input = modal.querySelector('input[type="text"], input:not([type="hidden"])')
  if (input) input.focus()
}

// The site script closes these modals (Escape, backdrop, after saving); focus
// then goes back to the button that opens it
function watchModal(modal) {
  if (modal.hasAttribute('data-mml-watched') || !window.MutationObserver) return
  modal.setAttribute('data-mml-watched', '')
  new MutationObserver(() => {
    if (modal.classList.contains('is-active')) return
    const active = document.activeElement
    if (active && active !== document.body && !modal.contains(active)) return
    const opener = document.querySelector('[data-mml-modal="' + modal.id + '"]')
    if (opener) opener.focus()
  }).observe(modal, { attributes: true, attributeFilter: ['class'] })
}

// ---------------------------------------------------------------- start page

const StartPage = {
  page: null,
  panel: null,
  adding: false,
  justAdded: null,

  init(page) {
    this.page = page
    this.panel = page.querySelector('[data-mml-start-panel]')
    this.adding = false
    this.justAdded = null
    if (this.panel) this.render()
  },

  render(focus) {
    const hasEntries = Mine.count() > 0
    this.page.classList.toggle('has-shades', hasEntries)

    if (!hasEntries) {
      this.panel.innerHTML =
        '<div class="mml-panel__intro">' +
          '<h2 class="mml-h2 mml-h2--lg" id="mml-picker-title" tabindex="-1">Which shade do you wear now?</h2>' +
          '<p class="mml-muted">Pick a foundation or concealer that suits you: brand, then product, then shade.</p>' +
        '</div>' +
        '<div class="mml-picker" data-mml-start-picker></div>'
      this.mountPicker()
    } else {
      let html =
        '<div class="mml-review">' +
          '<div class="mml-review__head"><h2 class="mml-h2 mml-h2--lg" id="mml-picker-title" tabindex="-1">Your shades</h2><span class="mml-count">' + Mine.count() + '</span></div>' +
          '<ul class="mml-entries mml-entries--lg">' + this.entriesHTML() + '</ul>' +
        '</div>'
      if (this.adding) {
        html += '<div class="mml-review__adding"><h3 class="mml-h3">Add another shade</h3><button type="button" class="mml-link" data-mml-start-cancel>Cancel</button></div>' +
          '<div class="mml-picker" data-mml-start-picker></div>'
      } else {
        html += '<button type="button" class="mml-add mml-add--lg" data-mml-start-add>' + ICON.plus + 'Add another shade you wear</button>' +
          '<div class="mml-review__foot">' +
            '<p class="mml-muted">Tip: add another shade you wear. Each one improves your matches.</p>' +
            '<a class="mml-primary" href="/">See my matches' + ICON.arrow + '</a>' +
          '</div>'
      }
      this.panel.innerHTML = html
      if (this.adding) this.mountPicker()
    }

    if (focus) {
      const element = this.panel.querySelector(focus)
      if (element) element.focus()
    }
  },

  entriesHTML() {
    const row = (entry, kind) => {
      const isNew = entry.key === this.justAdded
      const detail = kind === 'skintone' ? 'PANTONE SkinTone code' : entry.product + ' · ' + entry.brand
      return '<li class="mml-entry' + (isNew ? ' is-new' : '') + '" data-entry-key="' + esc(entry.key) + '">' +
        swatch(entry.hex, 'mml-entry__swatch') +
        '<span class="mml-entry__text"><span class="mml-entry__title"><span class="mml-entry__name">' + esc(entry.name) + '</span><span class="mml-tag">Added</span></span>' +
        '<span class="mml-entry__detail">' + esc(detail) + '</span></span>' +
        '<button type="button" class="mml-link mml-entry__remove" data-mml-start-remove="' + esc(entry.key) + '" data-mml-kind="' + kind + '" aria-label="Remove ' + esc(entry.name) + '">Remove</button></li>'
    }
    return Mine.shades.map((entry) => row(entry, 'shade')).join('') + Mine.skintones.map((entry) => row(entry, 'skintone')).join('')
  },

  mountPicker() {
    const host = this.panel.querySelector('[data-mml-start-picker]')
    new Picker(host, pickerOptions({
      onPick: (entry) => this.add(entry),
      onPantone: (code) => this.addCode(code)
    }))
  },

  showPanelTop() {
    const top = this.panel.getBoundingClientRect().top
    if (top < 0) this.panel.scrollIntoView({ block: 'start', behavior: 'smooth' })
  },

  added(entry, previous) {
    this.adding = false
    this.justAdded = entry.key
    this.render('#mml-picker-title')
    this.showPanelTop()
    Toast.show('Added ' + entry.name, () => this.undo(previous), () => {
      this.justAdded = null
      const row = this.panel.querySelector('.mml-entry.is-new')
      if (row) row.classList.remove('is-new')
    })
  },

  add(entry) {
    if (tooManyEntries()) return
    addEntry(entry)
      .then((previous) => this.added(entry, previous))
      .catch(() => Toast.show('Couldn’t add ' + entry.name + '. Check your connection and try again.'))
  },

  addCode(code) {
    if (tooManyEntries()) return Promise.resolve(true)
    const entry = { kind: 'skintone', key: code, name: 'PANTONE ' + code }
    return addEntry(entry).then((previous) => {
      if (!Mine.has('skintone', code)) return false
      this.added(Mine.find('skintone', code) || entry, previous)
      return true
    })
  },

  remove(kind, key) {
    const entry = Mine.find(kind, key)
    const name = entry ? entry.name : key
    removeEntry(kind, key).then((previous) => {
      this.justAdded = null
      if (!Mine.count()) this.adding = false
      this.render('#mml-picker-title')
      Toast.show(name + ' removed', () => this.undo(previous))
    }).catch(() => Toast.show('Couldn’t remove ' + name + '. Check your connection and try again.'))
  },

  undo(previous) {
    restoreEntries(previous).then(() => {
      this.justAdded = null
      this.adding = false
      this.render('#mml-picker-title')
    }).catch(() => Toast.show('Couldn’t undo. Check your connection and try again.'))
  }
}

// ---------------------------------------------------------------- wiring

function initPage(page) {
  Mine.set(readJSON(page, 'data-my-shades', {}))
  pickerConfig = readJSON(page, 'data-picker', pickerConfig)
  if (page.getAttribute('data-mml-page') === 'start') StartPage.init(page)
  Toast.ensure()
}

function plainClick(event) {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
}

document.addEventListener('click', (event) => {
  const target = event.target
  if (!target.closest || !target.closest('#mml-page')) return

  const opener = target.closest('[data-mml-open-picker]')
  if (opener) return Dialog.open(opener, opener.getAttribute('data-mml-open-picker') === 'pantone')

  const nav = target.closest('a[data-mml-nav]')
  if (nav && plainClick(event)) {
    event.preventDefault()
    return navigate(nav.getAttribute('href'))
  }

  const more = target.closest('a[data-mml-more]')
  if (more && plainClick(event)) {
    event.preventDefault()
    return showMore(more)
  }

  const reveal = target.closest('[data-mml-reveal]')
  if (reveal) return revealMore(reveal)

  const modal = target.closest('[data-mml-modal]')
  if (modal) return openModal(modal.getAttribute('data-mml-modal'))

  if (target.closest('[data-mml-start-add]')) {
    StartPage.adding = true
    StartPage.render(touchDevice() ? null : '#mml-q-brand')
    if (touchDevice()) {
      const adding = StartPage.panel.querySelector('.mml-review__adding')
      if (adding) adding.scrollIntoView({ block: 'start', behavior: 'smooth' })
    }
    return
  }
  if (target.closest('[data-mml-start-cancel]')) {
    StartPage.adding = false
    StartPage.render('[data-mml-start-add]')
    return
  }
  const startRemove = target.closest('[data-mml-start-remove]')
  if (startRemove) StartPage.remove(startRemove.getAttribute('data-mml-kind'), startRemove.getAttribute('data-mml-start-remove'))
})

document.addEventListener('submit', (event) => {
  const form = event.target
  if (!form.closest || !form.closest('#mml-page')) return

  if (form.hasAttribute('data-mml-remove')) {
    event.preventDefault()
    removeFromPage(form.getAttribute('data-mml-kind'), form.getAttribute('data-mml-remove'))
  } else if (form.hasAttribute('data-mml-start-over')) {
    event.preventDefault()
    startOver()
  } else if (form.hasAttribute('data-mml-add-shade')) {
    event.preventDefault()
    addFromShadePage(form)
  } else if (form.hasAttribute('data-mml-filters')) {
    event.preventDefault()
    navigate(filtersURL(form))
  }
})

document.addEventListener('change', (event) => {
  const field = event.target
  if (!field.matches) return
  if (field.matches('#mml-page [data-mml-nav-select]')) navigate(field.value)
  else if (field.matches('#mml-page [data-mml-autosubmit]') && field.form) navigate(filtersURL(field.form))
})

window.addEventListener('popstate', () => {
  const url = location.pathname + location.search
  if (url === shownURL || pageType() !== 'results') return
  shownURL = url
  refresh({ url: location.href }).catch(() => location.reload())
})

function boot() {
  const page = currentPage()
  if (page) initPage(page)
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot)
else boot()
